/**
 * Token Management Utility
 * Handles automatic token refresh and expiration checking
 */

const TOKEN_KEY = 'pos_token';
const TOKEN_EXPIRES_KEY = 'pos_token_expires';
const REFRESH_THRESHOLD_MINUTES = 60; // Refresh if token expires within 60 minutes

/**
 * Store token with expiration info
 */
export const storeToken = (token, expiresInDays = 30) => {
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + expiresInDays);
  
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt.toISOString());
  
  console.log('✅ Token stored, expires at:', expiresAt.toISOString());
};

/**
 * Get current token
 */
export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Check if token is expired or about to expire
 */
export const isTokenExpiring = () => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
  
  if (!expiresAt) {
    return true; // No expiration info = assume expired
  }
  
  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const minutesUntilExpiry = (expirationDate - now) / 1000 / 60;
  
  // Token is expiring if less than threshold minutes remaining
  return minutesUntilExpiry < REFRESH_THRESHOLD_MINUTES;
};

/**
 * Check if token is completely expired
 */
export const isTokenExpired = () => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
  
  if (!expiresAt) {
    return true;
  }
  
  const expirationDate = new Date(expiresAt);
  const now = new Date();
  
  return now > expirationDate;
};

/**
 * Clear token and related data
 */
export const clearToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_KEY);
  localStorage.removeItem('pos_user');
  localStorage.removeItem('restaurant_id');
  console.log('🗑️ Token cleared');
};

/**
 * Get time until token expires (in minutes)
 */
export const getTimeUntilExpiry = () => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
  
  if (!expiresAt) {
    return 0;
  }
  
  const expirationDate = new Date(expiresAt);
  const now = new Date();
  const minutesUntilExpiry = Math.max(0, (expirationDate - now) / 1000 / 60);
  
  return Math.floor(minutesUntilExpiry);
};

/**
 * Initialize token expiration from existing token
 * Call this on app startup if token exists but no expiration is stored
 */
export const initializeTokenExpiration = () => {
  const token = getToken();
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_KEY);
  
  if (token && !expiresAt) {
    // Token exists but no expiration info
    // Assume it was created recently and set 30 days from now
    storeToken(token, 30);
    console.log('⚠️ Token expiration initialized for existing token');
  }
};

export default {
  storeToken,
  getToken,
  isTokenExpiring,
  isTokenExpired,
  clearToken,
  getTimeUntilExpiry,
  initializeTokenExpiration
};
